import type { ReactNode } from 'react'
import { forwardRef } from 'react'

import { useSlotProps } from '~/utils/components/slots'
import { getFocusRing, useFocusStyle } from '~/utils/focus'
import { isVisualRegressionEnv } from '~/utils/is-visual-regression-env'
import { useTheme } from '~/utils/use-theme'

import { iconSizeMap } from './shared'
import type { IconProps, PrivateIconProps } from './shared'
import type { Props as BoxProps } from '../box/box'
import { Box } from '../box/box'
import { ConditionalWrapWithTooltip } from '../tooltip-internal/conditional-wrap-with-tooltip'

const getColor = ({
    colorProp,
    variant,
}: {
    colorProp: 'currentcolor' | IconProps['color']
    variant: IconProps['variant']
}): BoxProps['color'] => {
    if (variant === 'danger') return 'dangerActive'

    if (variant) return `${variant}Highlighted` as const

    return colorProp as BoxProps['color']
}

const parseViewBox = (viewBox: string) => {
    const match = /0 0 (\d+) (\d+)/.exec(viewBox)

    return {
        width: match ? Number.parseInt(match[1] as string, 10) : 24,
        height: match ? Number.parseInt(match[2] as string, 10) : 24,
    }
}

const CornerIconGroup = ({
    cornerIcons,
    viewBoxWidth,
    viewBoxHeight,
    cornerIconSize,
    cornerIconBackgroundColor = 'background' as const,
    cornerIconBackgroundShape,
}: {
    cornerIcons: IconProps['cornerIcons']
    viewBoxWidth: number
    viewBoxHeight: number
    cornerIconSize: number
    cornerIconBackgroundColor: IconProps['cornerIconBackgroundColor']
    cornerIconBackgroundShape: 'circle' | 'square' | 'none'
}) => {
    const theme = useTheme()
    const resolvedCornerColor =
        cornerIconBackgroundColor in theme.colors
            ? theme.colors[cornerIconBackgroundColor as keyof typeof theme.colors]
            : cornerIconBackgroundColor

    if (!cornerIcons) return null

    const scale = cornerIconSize / Math.max(viewBoxWidth, viewBoxHeight)
    const positions = [
        { key: 'topLeft', x: 0, y: 0 },
        { key: 'topRight', x: viewBoxWidth - cornerIconSize, y: 0 },
        { key: 'bottomLeft', x: 0, y: viewBoxHeight - cornerIconSize },
        { key: 'bottomRight', x: viewBoxWidth - cornerIconSize, y: viewBoxHeight - cornerIconSize },
    ]

    return (
        <>
            {positions.map(({ key, x, y }) => {
                const icon = cornerIcons[key as keyof typeof cornerIcons]

                if (!icon) return null

                const strokedIcon =
                    cornerIconBackgroundShape === 'none' ? (
                        icon
                    ) : (
                        <g
                            style={{
                                stroke: resolvedCornerColor,
                                strokeWidth: 7,
                                vectorEffect: 'non-scaling-stroke',
                            }}
                        >
                            {icon}
                        </g>
                    )

                return (
                    <g key={key} transform={`translate(${x}, ${y}) scale(${scale})`}>
                        {strokedIcon}
                        {icon}
                    </g>
                )
            })}
        </>
    )
}

const getCSS = (keepFillColor?: boolean, rotate?: number) =>
    keepFillColor
        ? {}
        : ({
              boxSizing: 'content-box',
              '& *[fill]:not([fill="none"]):not([data-corner-background])': {
                  fill: 'currentcolor',
              },
              '& *[stroke]': {
                  stroke: 'currentcolor',
              },
              transform: rotate ? `rotate(${rotate}deg)` : undefined,
          } as const)

/** @public this is our base to generate icons from */
export const Icon = forwardRef<HTMLElement, IconProps & PrivateIconProps>((props, ref) => {
    const {
        rotate,
        size = 'small',
        css,
        color: colorProp = 'currentcolor',
        variant,
        viewBox = '0 0 24 24',
        tooltip,
        children,
        keepFillColor,
        cornerIcons,
        cornerIconSize = 12,
        cornerIconBackgroundColor = 'background',
        cornerIconBackgroundShape = 'circle',
        ...slotProps
    } = useSlotProps(props, 'icon')

    const { width: viewBoxWidth, height: viewBoxHeight } = parseViewBox(viewBox)
    const iconColor = getColor({ colorProp, variant })
    const focusStyle = useFocusStyle(variant)

    const iconCss = [getCSS(keepFillColor, rotate), getFocusRing(focusStyle), css]

    return (
        <ConditionalWrapWithTooltip tooltip={tooltip} {...(isVisualRegressionEnv && { visible: true })}>
            <Box
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox={viewBox}
                ref={ref}
                as="svg"
                color={props.keepFillColor ? undefined : iconColor}
                overflow="visible"
                flexShrink={0}
                display="inline-block"
                height={iconSizeMap[size]}
                width={iconSizeMap[size]}
                tabIndex={tooltip ? 0 : undefined}
                css={iconCss}
                {...slotProps}
            >
                {children as ReactNode}
                <CornerIconGroup
                    cornerIcons={cornerIcons}
                    viewBoxWidth={viewBoxWidth}
                    viewBoxHeight={viewBoxHeight}
                    cornerIconSize={cornerIconSize}
                    cornerIconBackgroundColor={cornerIconBackgroundColor}
                    cornerIconBackgroundShape={cornerIconBackgroundShape}
                />
            </Box>
        </ConditionalWrapWithTooltip>
    )
})

Icon.displayName = 'Icon'
